import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PAYMENT_STATUS_STYLES = {
  succeeded: "bg-emerald-50 text-emerald-700",
  failed: "bg-[#D32F2F]/10 text-[#D32F2F]",
  pending: "bg-amber-50 text-amber-700",
};

const paymentStatusClass = (status) => PAYMENT_STATUS_STYLES[status] || "bg-slate-100 text-slate-700";

export default function Payments() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "cpa";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({ amount: 0, method: "card", purpose: "", status: "pending" });

  const load = useCallback(async () => {
    const { data } = await api.get("/payments");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount) });
      toast.success("Payment logged");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/payments/${toDelete.id}`);
      toast.success("Payment deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="payments-title">Payments</h1>
          <p className="text-slate-600 mt-1">Preparation fees, IRS payments and refunds.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="new-payment-btn"><Plus size={16} className="mr-1" /> Log payment</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Log payment</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Amount ($)</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
              <div><Label>Method</Label>
                <Select value={form.method} onValueChange={v => setForm({ ...form, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="card">Card</SelectItem><SelectItem value="ach">ACH</SelectItem><SelectItem value="check">Check</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Purpose</Label><Input value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="succeeded">Succeeded</SelectItem><SelectItem value="failed">Failed</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#0A2540]" data-testid="payment-submit">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border tf-hairline bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="payments-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Purpose</th>
              <th className="text-left p-3">Owner</th>
              <th className="text-left p-3">Method</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Status</th>
              {canDelete && <th />}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={canDelete ? 7 : 6} className="p-6 text-center text-slate-500">No payments yet.</td></tr>}
            {items.map(p => (
              <tr key={p.id} className="border-t tf-hairline hover:bg-slate-50">
                <td className="p-3 font-mono text-xs">{(p.created_at || "").split("T")[0]}</td>
                <td className="p-3">{p.purpose}</td>
                <td className="p-3">{p.owner_name}</td>
                <td className="p-3 uppercase text-xs font-mono">{p.method}</td>
                <td className="p-3 text-right font-mono">${Number(p.amount || 0).toLocaleString()}</td>
                <td className="p-3"><span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded ${paymentStatusClass(p.status)}`}>{p.status}</span></td>
                {canDelete && (
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setToDelete(p)} data-testid={`delete-payment-${p.id}`}>
                      <Trash size={14} className="text-[#D32F2F]" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete payment?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && `${toDelete.purpose} · $${Number(toDelete.amount || 0).toLocaleString()}`}. This permanently removes the payment record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-payment-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="delete-payment-confirm">
              Delete payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
